import CryptoJS from 'crypto-js';
import { PHONEPE_CONFIG } from '../config/phonepe.config';

interface PhonePePaymentRequest {
  orderId: string;
  amount: number; // in rupees
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  orderData?: any; // Optional: full order data to create order in Firestore
}

interface PhonePeResponse {
  success: boolean;
  code?: string;
  message?: string;
  data?: {
    merchantId: string;
    merchantTransactionId: string;
    instrumentResponse?: {
      type: string;
      redirectInfo?: {
        url: string;
        method: string;
      };
    };
  };
}

/**
 * PhonePe Payment Gateway Service
 * Documentation: https://developer.phonepe.com/v1/docs/payment-gateway
 */
export class PhonePeService {
  
  /**
   * Generate SHA256 hash for PhonePe request
   */
  private static generateChecksum(payload: string): string {
    const string = payload + '/pg/v1/pay' + PHONEPE_CONFIG.SALT_KEY;
    const sha256 = CryptoJS.SHA256(string);
    return sha256.toString(CryptoJS.enc.Hex) + '###' + PHONEPE_CONFIG.SALT_INDEX;
  }

  /**
   * Initiate PhonePe payment via Firebase Cloud Function
   */
  static async initiatePayment(request: PhonePePaymentRequest & { userId: string }): Promise<PhonePeResponse> {
    try {
      console.log('🚀 Initiating PhonePe payment request:', request);
      
      // Use the correct cloud function URL for this project
      const functionUrl = 'https://us-central1-rapsappweb-4f4b6.cloudfunctions.net/initiatePhonePePayment';
      
      console.log('📡 Calling cloud function:', functionUrl);

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          orderId: request.orderId,
          amount: request.amount,
          customerName: request.customerName,
          customerEmail: request.customerEmail,
          customerPhone: request.customerPhone,
          userId: request.userId,
          orderData: request.orderData // Send full order data if provided
        })
      });

      console.log('📨 Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [PHONEPE] Error response:', errorText);
        
        // Check for specific PhonePe authentication errors
        if (response.status === 401) {
          throw new Error('PhonePe payment gateway authentication failed. Please contact support or use Pay After Service option.');
        }
        
        throw new Error(`Payment gateway error (${response.status}): ${errorText}`);
      }

      const data = await response.json();
      console.log('✅ Payment response data:', data);

      if (data.success && data.data?.instrumentResponse?.redirectInfo) {
        return {
          success: true,
          data: data.data,
          message: data.message || 'Payment initiated successfully'
        };
      } else {
        throw new Error(data.message || 'Payment initiation failed');
      }
      
    } catch (error: any) {
      console.error('❌ PhonePe Payment Error:', error);
      return {
        success: false,
        message: error.message || 'Failed to initiate payment. Please try again or use Pay After Service.'
      };
    }
  }

  /**
   * Verify payment status via Firebase Cloud Function
   */
  static async verifyPayment(merchantTransactionId: string): Promise<PhonePeResponse> {
    try {
      // Get the current user's ID token for authentication
      // Call Firebase Cloud Function (no authentication required)
      const functionUrl = import.meta.env.VITE_VERIFY_FUNCTION_URL || 
        'https://us-central1-rapsappweb-4f4b6.cloudfunctions.net/verifyPhonePePayment';

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          merchantTransactionId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      return {
        success: data.success,
        code: data.data?.code,
        message: data.data?.message,
        data: data.data
      };
    } catch (error: any) {
      console.error('PhonePe Verification Error:', error);
      return {
        success: false,
        message: error.message || 'Failed to verify payment'
      };
    }
  }

  /**
   * Check if payment was successful
   */
  static isPaymentSuccessful(response: PhonePeResponse): boolean {
    return response.success && response.code === 'PAYMENT_SUCCESS';
  }

  /**
   * Get payment redirect URL
   */
  static getRedirectUrl(response: PhonePeResponse): string | null {
    return response.data?.instrumentResponse?.redirectInfo?.url || null;
  }
}

export default PhonePeService;
